import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authAPI, setTokens, clearTokens, isImpersonating, isDelegated } from '../../services/api';

export const login = createAsyncThunk('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const res = await authAPI.login(credentials);
    setTokens({ accessToken: res.data.accessToken, refreshToken: res.data.refreshToken });
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Login failed');
  }
});

export const fetchCurrentUser = createAsyncThunk('auth/fetchCurrentUser', async (_, { rejectWithValue }) => {
  try {
    const res = await authAPI.me();
    return res.data.user;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Failed to fetch user');
  }
});

export const logout = createAsyncThunk('auth/logout', async () => {
  // Both stand-in sessions live in their own tab, so leaving one means closing
  // that tab and returning to the session still open in the original.
  const wasScopedSession = isImpersonating() || isDelegated();
  try { await authAPI.logout(); } catch (e) { /* ignore */ }
  clearTokens();
  if (wasScopedSession) {
    try { window.close(); } catch { /* ignore */ }
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    isAuthenticated: false,
    loading: false,
    error: null,
    initializing: !!(isImpersonating() || isDelegated() ? sessionStorage : localStorage).getItem('accessToken'),
  },
  reducers: {
    clearError: (state) => { state.error = null; },
    clearMustChangePassword: (state) => {
      if (state.user) state.user.mustChangePassword = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false; state.isAuthenticated = true; state.user = action.payload.user;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })
      .addCase(fetchCurrentUser.pending, (state) => { state.loading = true; })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false; state.initializing = false; state.isAuthenticated = true; state.user = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.loading = false; state.initializing = false; state.isAuthenticated = false; state.user = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null; state.isAuthenticated = false; state.initializing = false;
      });
  },
});

export const { clearError, clearMustChangePassword } = authSlice.actions;
export default authSlice.reducer;
